import { YhcEFInputOneKazaiTypeDataObject } from './YhcEFInputOneKazaiTypeDataObject'
import { YhcEstimateFormFrontService } from './YhcEstimateFormFrontService'
import {
  ModelYhcKazaiDeliveryPeakSeasonRules,
  ModelYhcMastersByZipcode,
  ModelYhcOffices,
  ModelYhcPrefFromToInfos,
  YhcMastersByZipcodeTimeBand,
  YhcOptionsKey,
  ModelYhcSpecialLeadTimes,
  ModelYhcRegularWorkingDays,
  ModelFixedHolidays,
} from '../../fireModels/yhcDataTypes'
import {
  calcEarliestPickupDate,
  addingLeadTimeDaysByRegularWorkingDays,
  parseAdditionalLeadTimeDays,
  calcEarliestPickupDateWithFixedHolidays,
  adjustDeliveryDateForFixedHolidays,
  adjustDeliveryDateForFixedHolidaysAndRegularWorkingDays,
} from './regularWorkingDaysCalc'
import { reactive } from 'vue'
import {
  yhcAreaIsInHokkaido,
  yhcAreaIsInOkinawa,
  yhcAreasByCode,
} from '../../constants/area'
import { findById, findRecordsByModelName } from './findRecordsByModelName'
import { YhcEFInputOneKazaiTypeDataObjectForKaguidoOption } from './YhcEFInputOneKazaiTypeDataObjectForKaguidoOption'
import { judgementFixedHoliday } from '../fixedHolidaysJudgeFunction'
import {
  fetchVersionBasedOnDatetime,
  pickEarliestVisibleServiceLevelDate,
  pickEstimateFormDeliveryDateForVersion,
} from './masterVersionByDate'
import { findVersionIdCoveringDate } from '../YhcServiceLevelList/masterVersionSpanNotes'
import {
  findNextWorkingDaysChangeYmd,
  findNextEnabledCalendarYmd,
  isCalendarDateDisabled,
  isRegularNonWorkingDay,
  resolveRegularWeekdaysForDate,
  pickZipRecordForVersion,
  zipLookupCandidates,
  zipMasterRecordIdsForLookup,
  toCalendarYmd,
  versionIdOfZipRecord,
  normalizeZipCacheKey,
  pickCoveringWorkingDayRecord,
  pickFirstDefined,
} from './workingDaysByDate'
import { specialLeadTimeFilters } from './specialLeadTimesPick'
import type { CorporationRankDisplayMode } from '../YhcServiceLevelList/utils/yhcServiceLevelRankDisplayMode'
import { formatRevisionDateForNote } from '../YhcServiceLevelList/serviceLevelRevisionUtils'

const dateDisplayErrorMessages = {
  invalidZipCode: '郵便番号が正しくありません',
  masterVersionNotFound: '指定日のマスタバージョンが見つかりません',
  zipNotInMasterVersion: '指定日のマスタに該当郵便番号がありません',
  unavailableArea: 'サービスエリア外',
  unavailableBecauseOfIncludingLargeSizeKazai: 'サービスエリア外',
  needDateAdjustDueToHokkaido: 'お申し込み時に日程調整が必要',
  needDateAdjustDueToHokkaidoOrOkinawa: 'お申し込み時に日程調整が必要',
  ytcNeedDateAdjust: 'ご依頼時に調整',
}

/**
 * 見積りフォーム上 ユーザの入力値を保持, 監視しつつ 料金計算用のレコードを表示するなど実施する
 *
 * ## 責任範囲
 * - 郵便番号の入力を保持
 * - 郵便番号のマスタデータレコードを保持
 * - 配送元, 先の時間帯指定を保持
 * - 2024.10.15追加 家具移動オプションの移動先質問と回答を保持
 * - 合計金額の算出
 *
 */
export class YhcEFUserInputDataHandler {
  private _currentDatePeakSeasonRule: ModelYhcKazaiDeliveryPeakSeasonRules | undefined =
    undefined
  zip_from: string = ''
  zip_from_yhc_master: ModelYhcMastersByZipcode | null = null
  from_time_option: YhcEFTimeOptionKey = 'none' // 時間帯の指定状態を保持
  from_time_option_use_jit: boolean = false // ジャスト・イン・タイム のオプションの利用 or not
  zip_to: string = ''
  zip_to_yhc_master: ModelYhcMastersByZipcode | null = null
  to_time_option: YhcEFTimeOptionKey = 'none' // 時間帯の指定状態を保持
  to_time_option_use_jit: boolean = false // ジャスト・イン・タイム のオプションの利用 or not
  pickupDateResetFlag: boolean = false // お預かり日がリセットされたかどうかのフラグ
  pickupSelectedDate: string | null = $core
    .$dayjs()
    .add(minPickupNeedDays(false), 'day')
    .format('YYYY-MM-DD')
  pickupSelectedDateTemp: string | null = null // 当日自発着利用時の元お預かり日保存用
  isSameDayPickupAndDelivery: boolean = false // 当日自発着の利用有無
  /** サービスレベル検索：発着選択 'pickup'=お預かり日指定 / 'delivery'=お届け日指定 */
  serviceLevelDateMode: 'pickup' | 'delivery' = 'pickup'
  /** サービスレベル検索：お届け日指定モードで選択したお届け日（未選択時は null） */
  deliverySelectedDate: string | null = null
  /** サービスレベル検索：お届け先変更によりお届け日が営業日でなくなったときに true（再選択を促す） */
  deliveryDateResetFlag: boolean = false
  /** サービスレベル検索：画面上のランク表示（非表示ランクの日付は版の判定に使わない） */
  serviceLevelRankDisplayMode: CorporationRankDisplayMode = 'both'
  from_office_master: ModelYhcOffices | null = null
  to_office_master: ModelYhcOffices | null = null
  from_regular_working_days_master: ModelYhcRegularWorkingDays | null = null
  to_regular_working_days_master: ModelYhcRegularWorkingDays | null = null
  /** カレンダー用: 発地 zip の版ID → 営業日パターン名 */
  fromWorkingDayPatternByVersionId: Record<string, string> = {}
  /** カレンダー用: 着地 zip の版ID → 営業日パターン名 */
  toWorkingDayPatternByVersionId: Record<string, string> = {}
  /** カレンダー用: 発着パターン名に紐づく営業日レコード（有効期間つき） */
  regularWorkingDayRecords: ModelYhcRegularWorkingDays[] = []
  /** zip の全版レコード（住所マスタはここから版 ID で選ぶ） */
  fromZipRecords: ModelYhcMastersByZipcode[] = []
  toZipRecords: ModelYhcMastersByZipcode[] = []
  /** zip 入力欄の再取得中。handler の zip マスタは残し、入力欄側だけ住所を隠す */
  zip_from_pending = false
  zip_to_pending = false
  private _masterRefreshGeneration = 0
  private _isRefreshingMasters = false
  private _cachedFromZipKey = ''
  private _cachedToZipKey = ''
  private _loadedPrefSpecialLtKey = ''
  private _loadedOfficeStoreKey = ''
  private _fetchedWorkingDayPatternNames = new Set<string>()

  get pref_from(): string {
    return this.zip_from_yhc_master?.prefCode || ''
  }
  get minPickupNeedDays(): number {
    // 法人向けでは繁忙期ルールを適用せず、常に通常期の日数で算出
    const peakRule = this.$efs.isCorporationView
      ? undefined
      : this._currentDatePeakSeasonRule
    return minPickupNeedDays(this.isSameDayPickupAndDelivery, peakRule)
  }
  get minPickupNeedDate(): string {
    return $core.$dayjs().add(this.minPickupNeedDays, 'day').format('YYYY-MM-DD')
  }

  get pref_from_name(): string {
    if (!this.zip_from_yhc_master) {
      return ''
    }
    const area = yhcAreasByCode[this.zip_from_yhc_master?.prefCode]
    return area?.prefName || area?.name || ''
  }
  get address_from(): string {
    return this.pref_from_name
      ? [
          this.pref_from_name,
          this.zip_from_yhc_master?.city || '',
          this.zip_from_yhc_master?.town || '',
        ].join(' ')
      : ''
  }

  get pref_to(): string {
    return this.zip_to_yhc_master?.prefCode || ''
  }
  get pref_to_name(): string {
    if (!this.zip_to_yhc_master) {
      return ''
    }
    const area = yhcAreasByCode[this.zip_to_yhc_master.prefCode]
    return area?.prefName || area?.name || ''
  }
  get address_to(): string {
    return this.pref_to_name
      ? [
          this.pref_to_name,
          this.zip_to_yhc_master?.city || '',
          this.zip_to_yhc_master?.town || '',
        ].join(' ')
      : ''
  }

  /**
   * from, to郵便番号から事業所マスタのフェッチ
   * ※サービスレベル検索のための機能
   */
  public async fetchFromToOffices(isStale: () => boolean = () => false) {
    const fromCode = this.zip_from_yhc_master?.kazaiStoreCode || ''
    const toCode = this.zip_to_yhc_master?.kazaiStoreCode || ''
    const storeKey = `${fromCode}|${toCode}`
    if (storeKey === this._loadedOfficeStoreKey) {
      if (!fromCode) this.from_office_master = null
      if (!toCode) this.to_office_master = null
      return
    }

    const [fromOffices, toOffices] = await Promise.all([
      fromCode
        ? findRecordsByModelName('yhc_offices', {
            filter: { code: { _eq: fromCode } },
          })
        : Promise.resolve([]),
      toCode
        ? findRecordsByModelName('yhc_offices', {
            filter: { code: { _eq: toCode } },
          })
        : Promise.resolve([]),
    ])
    if (isStale()) return
    this.from_office_master = fromOffices[0] || null
    this.to_office_master = toOffices[0] || null
    this._loadedOfficeStoreKey = storeKey
  }

  /**
   * 営業日パターンマスタのフェッチ
   * @param isMer メルカリ用かどうか（サービスレベル検索ページに利用）
   */
  public async fetchFromToRegularWorkingDays(isMer = false) {
    const pickupDate =
      this.getPickupDateForVersion() ||
      toCalendarYmd(this.pickupSelectedDate) ||
      toCalendarYmd(this.$efs.baseDatetime)
    const deliveryDate =
      this.getDeliveryDateForVersion() ||
      pickupDate ||
      toCalendarYmd(this.$efs.baseDatetime)

    if (this.zip_from_yhc_master && this.zip_from_yhc_master.regularWorkingDay) {
      const fromCovering = pickCoveringWorkingDayRecord(
        this.regularWorkingDayRecords,
        this.zip_from_yhc_master.regularWorkingDay,
        pickupDate,
      ) as ModelYhcRegularWorkingDays | null
      this.from_regular_working_days_master =
        isMer && fromCovering?.fallback_pattern_name
          ? (fromCovering.fallback_pattern_name as ModelYhcRegularWorkingDays)
          : fromCovering
      await this.recalculateEarliestPickupDate()
    } else {
      this.from_regular_working_days_master = null
    }

    if (this.zip_to_yhc_master && this.zip_to_yhc_master.regularWorkingDay) {
      const toCovering = pickCoveringWorkingDayRecord(
        this.regularWorkingDayRecords,
        this.zip_to_yhc_master.regularWorkingDay,
        deliveryDate,
      ) as ModelYhcRegularWorkingDays | null
      this.to_regular_working_days_master =
        isMer && toCovering?.fallback_pattern_name
          ? (toCovering.fallback_pattern_name as ModelYhcRegularWorkingDays)
          : toCovering
    } else {
      this.to_regular_working_days_master = null
    }
  }

  /**
   * カレンダー／お届け可能日リスト用に、発着 zip の全版パターンと営業日レコードを先読みする。
   * お預かり日未選択でもカレンダーを開くため、版取り直しの前に呼ぶ。
   */
  public async fetchWorkingDaysCalendarIndex(
    isStale: () => boolean = () => false,
  ): Promise<void> {
    const fromKey = normalizeZipCacheKey(this.zip_from)
    const toKey = normalizeZipCacheKey(this.zip_to)

    const [fromRecords, toRecords] = await Promise.all([
      !fromKey
        ? Promise.resolve([] as ModelYhcMastersByZipcode[])
        : fromKey === this._cachedFromZipKey
          ? Promise.resolve(this.fromZipRecords)
          : this.fetchZipRecordsByZip(this.zip_from),
      !toKey
        ? Promise.resolve([] as ModelYhcMastersByZipcode[])
        : toKey === this._cachedToZipKey
          ? Promise.resolve(this.toZipRecords)
          : this.fetchZipRecordsByZip(this.zip_to),
    ])
    if (isStale()) return

    const fromMap = this.workingDayPatternMapFromZipRecords(fromRecords)
    const toMap = this.workingDayPatternMapFromZipRecords(toRecords)
    this.fromZipRecords = fromRecords
    this.toZipRecords = toRecords
    this._cachedFromZipKey = fromKey
    this._cachedToZipKey = toKey
    this.fromWorkingDayPatternByVersionId = fromMap
    this.toWorkingDayPatternByVersionId = toMap

    const patternNames = [
      ...new Set(
        [...Object.values(fromMap), ...Object.values(toMap)].filter((name) => !!name),
      ),
    ]
    if (!fromKey && !toKey) {
      this.regularWorkingDayRecords = []
      this._fetchedWorkingDayPatternNames.clear()
      return
    }
    const existingNames = new Set([
      ...this._fetchedWorkingDayPatternNames,
      ...this.regularWorkingDayRecords
        .map((record) => String(record.pattern_name || '').trim())
        .filter((name) => !!name),
    ])
    const missingNames = patternNames.filter((name) => !existingNames.has(name))
    if (missingNames.length === 0) return

    const extraRecords =
      (await findRecordsByModelName('yhc_regular_working_days', {
        filter: { pattern_name: { _in: missingNames } },
        fields: ['*', 'fallback_pattern_name.*'],
        limit: -1,
      })) || []
    if (isStale()) return
    for (const name of missingNames) this._fetchedWorkingDayPatternNames.add(name)
    this.mergeRegularWorkingDayRecords(extraRecords)
  }

  private mergeRegularWorkingDayRecords(extra: ModelYhcRegularWorkingDays[]) {
    if (!extra.length) return
    const existingKeys = new Set(
      this.regularWorkingDayRecords.map(
        (record) =>
          `${record.pattern_name || ''}|${record.enabled_from || ''}|${record.disabled_at || ''}`,
      ),
    )
    for (const record of extra) {
      const key = `${record.pattern_name || ''}|${record.enabled_from || ''}|${record.disabled_at || ''}`
      if (existingKeys.has(key)) continue
      this.regularWorkingDayRecords.push(record)
      existingKeys.add(key)
    }
  }

  private workingDayPatternMapFromZipRecords(
    records: ModelYhcMastersByZipcode[],
  ): Record<string, string> {
    const map: Record<string, string> = {}
    for (const record of records) {
      const versionId = versionIdOfZipRecord(record)
      if (!versionId) continue
      map[versionId] = String(record.regularWorkingDay || '').trim()
    }
    return map
  }

  private async fetchWorkingDayPatternByVersionId(
    zip: string,
  ): Promise<Record<string, string>> {
    return this.workingDayPatternMapFromZipRecords(await this.fetchZipRecordsByZip(zip))
  }

  private async fetchZipRecordsByZip(zip: string): Promise<any[]> {
    const candidates = zipLookupCandidates(zip)
    if (candidates.length === 0) return []
    const ids = zipMasterRecordIdsForLookup(zip, this.$efs.masterVersionPeriods)
    return (
      (await findRecordsByModelName('yhc_by_zipcodes', {
        filter: ids.length > 0 ? { id: { _in: ids } } : { zip: { _in: candidates } },
        limit: -1,
      })) || []
    )
  }

  private coveringMasterVersionId(date: string | Date | null | undefined): string {
    const ymd = toCalendarYmd(date)
    if (!ymd) return ''
    return findVersionIdCoveringDate(this.$efs.masterVersionPeriods, ymd) || ''
  }

  private findZipMasterForVersion(
    zip: string,
    versionId: string,
    cachedRecords?: ModelYhcMastersByZipcode[],
  ): ModelYhcMastersByZipcode | null {
    if (!zip) return null
    const resolvedVersionId =
      versionId || this.coveringMasterVersionId(this.$efs.baseDatetime)
    if (!resolvedVersionId) return null
    return pickZipRecordForVersion(
      cachedRecords || [],
      resolvedVersionId,
      this.$efs.masterVersionPeriods,
    )
  }

  public getPickupWorkingWeekdaysForDate(
    date: string | Date | null | undefined,
  ): string[] {
    return resolveRegularWeekdaysForDate(date, {
      periods: this.$efs.masterVersionPeriods,
      patternNameByVersionId: this.fromWorkingDayPatternByVersionId,
      records: this.regularWorkingDayRecords,
      useFallback: this.$efs.isFleaMarket,
    })
  }

  public getDeliveryWorkingWeekdaysForDate(
    date: string | Date | null | undefined,
  ): string[] {
    return resolveRegularWeekdaysForDate(date, {
      periods: this.$efs.masterVersionPeriods,
      patternNameByVersionId: this.toWorkingDayPatternByVersionId,
      records: this.regularWorkingDayRecords,
      useFallback: this.$efs.isFleaMarket,
    })
  }

  public isPickupWorkingDayForDate(date: string | Date | null | undefined): boolean {
    return !isCalendarDateDisabled(
      date,
      this.getPickupWorkingWeekdaysForDate(date),
      this.fixed_holidays,
    )
  }

  public isDeliveryWorkingDayForDate(date: string | Date | null | undefined): boolean {
    return !isCalendarDateDisabled(
      date,
      this.getDeliveryWorkingWeekdaysForDate(date),
      this.fixed_holidays,
    )
  }

  public isPickupCalendarDateDisabled(date: string | Date | null | undefined): boolean {
    return !this.isPickupWorkingDayForDate(date)
  }

  public isDeliveryCalendarDateDisabled(date: string | Date | null | undefined): boolean {
    return !this.isDeliveryWorkingDayForDate(date)
  }

  pref_from_to_info_master: ModelYhcPrefFromToInfos | null = null
  special_lead_times: ModelYhcSpecialLeadTimes | null = null
  fixed_holidays: ModelFixedHolidays[] = []
  deliveryDateAdjustedForFixedHolidays: string = ''
  fgDeliveryDateAdjustedForFixedHolidays: string = ''
  remoteIslandDeliveryDateAdjusted: string = ''
  userAddedKazaiDataById: { [id: string]: YhcEFInputOneKazaiTypeDataObject } = {}
  userAddedKazaiDataByIdForKaguidoOptionCollectionDestination: {
    [id: string]: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
  } = {}
  userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination: {
    [id: string]: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
  } = {}
  private _autoSaveIntervalId: any = null
  deliveryPeakSeasonRule: ModelYhcKazaiDeliveryPeakSeasonRules | undefined = undefined
  /** お預かり日が繁忙期期間に掛かっているか（時間帯指定可否の判定に使用） */
  pickupDateInPeakSeason: boolean = false
  /** お届け日が繁忙期期間に掛かっているか（時間帯指定可否の判定に使用） */
  deliveryDateInPeakSeason: boolean = false

  constructor(
    public readonly $efs: YhcEstimateFormFrontService, // 親サービスへの参照 (循環参照になる)
  ) {
    // auto save & restore if config is set
    if (this.$efs.enableAutoSaveOfUserInputData) {
      console.log(
        '[YhcEFUserInputDataHandler] Auto save enabled!, restore from local storage & start auto save interval',
      )
      this.restoreStateFromLocalStorage()
      this.kickAutoSaveStateToLocalStorage()
    }
    // 現在日の繁忙期状態を初期化
    this.initCurrentDatePeakSeasonRule()
    // お預かり日～お届け日が繁忙期であるかどうか初期設定
    this.loadDeliveryPeakSeasonRules()
    this.adjustDeliveryDatesForFixedHolidays() // 年末年始休業日と定休日を考慮したお届け日を調整
  }

  /**
   * 現在日の繁忙期状態を初期化
   * @private
   */
  private async initCurrentDatePeakSeasonRule() {
    this._currentDatePeakSeasonRule = await this._getCurrentDatePeakSeasonRule()
  }

  /**
   * 年末年始休業日マスタのフェッチ
   */
  public async fetchFixedHolidays() {
    this.fixed_holidays =
      (await findRecordsByModelName('fixed_holidays', {
        filter: {},
      })) || []
  }

  /**
   * お届け日関連の再計算
   * - 家財追加/削除時に呼び出して、お届け日を再計算する
   */
  public async recalculateDeliveryDates(): Promise<void> {
    await this.refreshMastersForSelectedDates()
  }

  /**
   * 初期お預かり日を年末年始休業日を考慮して調整
   */
  public async adjustInitialPickupDate() {
    if (!this.pickupSelectedDate) return

    const currentDateObj = $core.$dayjs(this.pickupSelectedDate).toDate()
    const isHoliday = await judgementFixedHoliday(currentDateObj)

    if (isHoliday) {
      this.pickupSelectedDate = await calcEarliestPickupDateWithFixedHolidays(
        this.pickupSelectedDate,
        this.from_regular_working_days_master,
      )
    }
  }

  /**
   * 営業日パターンマスタを考慮した最短お預かり日を計算し、設定する
   * 初期日が定休・年末年始なら、未選択にせず翌営業日を初期表示する
   * @private
   */
  private async recalculateEarliestPickupDate(): Promise<void> {
    const minDate = $core.$dayjs().add(this.minPickupNeedDays, 'day').format('YYYY-MM-DD')

    if (this.from_regular_working_days_master) {
      const start = this.pickupSelectedDate || minDate
      this.pickupSelectedDate =
        findNextEnabledCalendarYmd(
          start,
          (ymd) => !this.isPickupWorkingDayForDate(ymd),
        ) || start
      this.pickupDateResetFlag = false
    } else {
      this.pickupSelectedDate = minDate
      this.pickupDateResetFlag = false
    }
  }

  /**
   * お届け日が年末年始休業日の場合、休業明けの営業日に調整する
   */
  public async adjustDeliveryDatesForFixedHolidays() {
    if (this.deliveryDate) {
      this.deliveryDateAdjustedForFixedHolidays =
        await adjustDeliveryDateForFixedHolidays(this.deliveryDate)
    } else {
      this.deliveryDateAdjustedForFixedHolidays = ''
    }

    if (this.fgDeliveryDate) {
      this.fgDeliveryDateAdjustedForFixedHolidays =
        await adjustDeliveryDateForFixedHolidays(this.fgDeliveryDate)
    } else {
      this.fgDeliveryDateAdjustedForFixedHolidays = ''
    }

    const isRemoteIsland =
      this.zip_from_yhc_master?.isRemoteIsland === true ||
      this.zip_to_yhc_master?.isRemoteIsland === true

    if (isRemoteIsland && this.pickupSelectedDate) {
      const remoteIslandDate = $core
        .$dayjs(this.pickupSelectedDate)
        .add(14, 'day')
        .format('YYYY-MM-DD')
      this.remoteIslandDeliveryDateAdjusted =
        await adjustDeliveryDateForFixedHolidaysAndRegularWorkingDays(
          remoteIslandDate,
          this.to_regular_working_days_master,
        )
    } else {
      this.remoteIslandDeliveryDateAdjusted = ''
    }
  }

  /**
   * お届け日指定モードで、選択中のお届け日がお届け先の営業日でない場合にクリアして再選択を促す。
   * サービスレベル検索のお届け先（zip_to）変更時のみ、YhcServiceLevel.vue の @update 内で呼ぶこと。
   */
  public async checkDeliveryDateBusinessDayAndResetIfNeeded(): Promise<void> {
    if (
      !this.$efs.isServiceLevel ||
      this.serviceLevelDateMode !== 'delivery' ||
      !this.deliverySelectedDate
    ) {
      return
    }
    const d = this.deliverySelectedDate
    const isRegularWorkingDay = !isRegularNonWorkingDay(
      d,
      this.getDeliveryWorkingWeekdaysForDate(d),
    )

    const dateObj = $core.$dayjs(d).toDate()
    const isHoliday = await judgementFixedHoliday(dateObj)

    if (!isRegularWorkingDay || isHoliday) {
      this.deliverySelectedDate = null
      this.deliveryDateResetFlag = true
    }
  }

  /**
   * 繁忙期シーズンルールマスタの取得
   * - フェッチは親サービス（YhcEstimateFormFrontService）で実施済み
   * - 画面表示時及びお預かり日変更時に実行する
   * - 2025.03.11 さらにサイズランクによる追加日数を加味するため家財追加時にも実行
   */
  public async loadDeliveryPeakSeasonRules() {
    // 現在日の繁忙期状態を更新
    this._currentDatePeakSeasonRule = await this._getCurrentDatePeakSeasonRule()

    // 選択された日付の繁忙期状態を判定（料金計算用）
    this.deliveryPeakSeasonRule = await this.$efs.deliveryPeakSeasonRules.find((rule) => {
      const pickup = this.pickupSelectedDate
      return (
        (pickup != null && pickup >= rule.startDate && pickup <= rule.endDate) ||
        (this.deliveryDate >= rule.startDate && this.deliveryDate <= rule.endDate) ||
        (pickup != null && pickup <= rule.startDate && this.deliveryDate >= rule.endDate)
      )
    })

    // お預かり日・お届け日それぞれが繁忙期に掛かっているか（時間帯指定可否の判定用）
    const toDateOnly = (d: string) => d.slice(0, 10)
    this.pickupDateInPeakSeason = this.$efs.deliveryPeakSeasonRules.some((rule) => {
      const s = toDateOnly(rule.startDate)
      const e = toDateOnly(rule.endDate)
      const pickup = this.pickupSelectedDate
      return pickup != null && pickup >= s && pickup <= e
    })
    this.deliveryDateInPeakSeason = this.$efs.deliveryPeakSeasonRules.some((rule) => {
      const s = toDateOnly(rule.startDate)
      const e = toDateOnly(rule.endDate)
      return !!this.deliveryDate && this.deliveryDate >= s && this.deliveryDate <= e
    })

    // 選択された日付が現在の最短お預かり可能日より前の場合は調整
    await this._changePickupDateByPeakSeasonRule()
  }

  /**
   * 現在日の繁忙期判定
   * @private
   */
  private async _getCurrentDatePeakSeasonRule() {
    const today = $core.$dayjs().format('YYYY-MM-DD')
    return this.$efs.deliveryPeakSeasonRules.find((rule) => {
      return today >= rule.startDate && today <= rule.endDate
    })
  }

  /**
   * 繁忙期シーズンルールマスタに沿ったお預かり日の変更
   * - サービスレベル検索の通常モードでは選択した日付をそのまま採用（過去日選択可）。フリマ用は翌々日以降に揃える。
   */
  private async _changePickupDateByPeakSeasonRule() {
    if (this.$efs.isServiceLevel && !this.$efs.isFleaMarket) return
    if (this.$efs.isCorporationView) return

    if (
      this.deliveryPeakSeasonRule &&
      this.pickupSelectedDate != null &&
      this.pickupSelectedDate <= this.minPickupNeedDate
    ) {
      const now = $core.$dayjs()
      const hour = now.hour()
      const additionalDays =
        hour < 15
          ? this.deliveryPeakSeasonRule.additionalLeadDays + 1
          : this.deliveryPeakSeasonRule.additionalLeadDays + 2

      let newPickupDate = $core.$dayjs().add(additionalDays, 'day').format('YYYY-MM-DD')

      newPickupDate = await calcEarliestPickupDateWithFixedHolidays(
        newPickupDate,
        this.from_regular_working_days_master,
      )

      this.pickupSelectedDate = newPickupDate
    }
  }

  public addKazaiData(kazaiData: YhcEFInputOneKazaiTypeDataObject) {
    this.userAddedKazaiDataById[kazaiData.id] = reactive(
      kazaiData,
    ) as YhcEFInputOneKazaiTypeDataObject
  }

  public updateKazaiData(kazaiData: YhcEFInputOneKazaiTypeDataObject) {
    this.addKazaiData(kazaiData) // id で更新するので add と 同じ
  }

  public removeKazaiDataById(id: string) {
    delete this.userAddedKazaiDataById[id]
  }

  // 2024.10.15追加 家具移動オプションの追加家財データ関連
  public addKazaiDataForKaguidoOptionCollectionDestination(
    kazaiData: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption,
  ) {
    this.userAddedKazaiDataByIdForKaguidoOptionCollectionDestination[kazaiData.id] =
      reactive(kazaiData) as YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
  }

  public updateKazaiDataForKaguidoOptionCollectionDestination(
    kazaiData: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption,
  ) {
    this.addKazaiDataForKaguidoOptionCollectionDestination(kazaiData) // id で更新するので add と 同じ
  }

  public removeKazaiDataByIdForKaguidoOptionCollectionDestination(id: string) {
    delete this.userAddedKazaiDataByIdForKaguidoOptionCollectionDestination[id]
  }

  // お届け先の家具移動オプション関連
  public addKazaiDataForKaguidoOptionDeliveryDestination(
    kazaiData: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption,
  ) {
    this.userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination[kazaiData.id] =
      reactive(kazaiData) as YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
  }

  public updateKazaiDataForKaguidoOptionDeliveryDestination(
    kazaiData: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption,
  ) {
    this.addKazaiDataForKaguidoOptionDeliveryDestination(kazaiData) // id で更新するので add と 同じ
  }

  public removeKazaiDataByIdForKaguidoOptionDeliveryDestination(id: string) {
    delete this.userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination[id]
  }

  /**
   * 郵便番号の入力値を更新しつつ、マスタデータを取得し、保持する
   */
  public async updateZip(zip: string, fromOrTo: 'zip_from' | 'zip_to') {
    const masterKey =
      fromOrTo === 'zip_from' ? 'zip_from_yhc_master' : 'zip_to_yhc_master'
    if (this[fromOrTo] === zip && this[masterKey]) {
      return
    }
    if (fromOrTo === 'zip_from') this.zip_from_pending = true
    else this.zip_to_pending = true
    this[fromOrTo] = zip
    await this.refreshMastersForSelectedDates()
  }

  public async loadZipMaster(isStale: () => boolean = () => false) {
    const pickupVersion = this.$efs.versionPickup
    const deliveryVersion = this.$efs.versionDelivery || pickupVersion

    this.zip_from_yhc_master = this.findZipMasterForVersion(
      this.zip_from,
      pickupVersion,
      this.fromZipRecords,
    )
    this.zip_to_yhc_master = this.findZipMasterForVersion(
      this.zip_to,
      deliveryVersion,
      this.toZipRecords,
    )
    const routeKey = this.prefSpecialLtCacheKey()
    if (routeKey && routeKey === this._loadedPrefSpecialLtKey) {
      return
    }

    if (this.pref_from && this.pref_to && pickupVersion) {
      const [prefMaster, specialLeadTimes] = await Promise.all([
        findById(
          'yhc_pref_from_to_infos',
          `${pickupVersion}--${this.pref_from}-${this.pref_to}`,
        ),
        this.zip_from_yhc_master && this.zip_to_yhc_master
          ? this._fetchSpecialLeadTimes()
          : Promise.resolve(null),
      ])
      if (isStale()) return
      this.pref_from_to_info_master = prefMaster
      this.special_lead_times = specialLeadTimes
      this._loadedPrefSpecialLtKey = routeKey
    } else {
      this.pref_from_to_info_master = null
      this.special_lead_times = null
      this._loadedPrefSpecialLtKey = routeKey
    }
  }

  private prefSpecialLtCacheKey(): string {
    const from = this.zip_from_yhc_master
    const to = this.zip_to_yhc_master
    return [
      this.$efs.versionPickup || '',
      this.$efs.versionDelivery || '',
      from?.prefCode || '',
      from?.city || '',
      from?.town || '',
      to?.prefCode || '',
      to?.city || '',
      to?.town || '',
      from ? '1' : '0',
      to ? '1' : '0',
    ].join('|')
  }

  /**
   * お預かり日・お届け日に応じたマスタバージョンを取得し、郵便番号・営業日マスタを取り直す
   */
  public async refreshMastersForSelectedDates(): Promise<void> {
    const generation = ++this._masterRefreshGeneration
    const isStale = () => generation !== this._masterRefreshGeneration

    this._isRefreshingMasters = true
    const showOverlay = !this.$efs.isLoading
    if (showOverlay) {
      $core.$loading.start('データ取得中...', 'overlay')
    }
    try {
      await this.fetchWorkingDaysCalendarIndex(isStale)
      if (isStale()) return

      const pickupDate =
        this.getPickupDateForVersion() ||
        toCalendarYmd(this.pickupSelectedDate) ||
        toCalendarYmd(this.$efs.baseDatetime)
      if (!pickupDate) {
        return
      }

      const pickupVersion =
        findVersionIdCoveringDate(this.$efs.masterVersionPeriods, pickupDate) ||
        (await fetchVersionBasedOnDatetime(pickupDate)) ||
        ''
      if (isStale()) return
      this.$efs.versionPickup = pickupVersion
      this.$efs.versionDelivery = pickupVersion

      await this.loadZipMaster(isStale)
      if (isStale()) return
      await this.fetchFromToRegularWorkingDays(this.$efs.isFleaMarket)
      if (isStale()) return
      await this.adjustDeliveryDatesForFixedHolidays()
      if (isStale()) return

      for (let i = 0; i < 3; i++) {
        const deliveryDate = this.getDeliveryDateForVersion() || pickupDate
        const deliveryVersion =
          findVersionIdCoveringDate(this.$efs.masterVersionPeriods, deliveryDate) ||
          (await fetchVersionBasedOnDatetime(deliveryDate)) ||
          this.$efs.versionPickup
        if (isStale()) return

        let nextPickupVersion = this.$efs.versionPickup
        if (
          this.$efs.isServiceLevel &&
          this.serviceLevelDateMode === 'delivery' &&
          this.deliverySelectedDate &&
          !this.deliveryDateResetFlag
        ) {
          const earliestPickup = this.findEarliestVisiblePickupCandidateDate(
            this.deliverySelectedDate,
          )
          if (earliestPickup) {
            nextPickupVersion =
              findVersionIdCoveringDate(this.$efs.masterVersionPeriods, earliestPickup) ||
              (await fetchVersionBasedOnDatetime(earliestPickup)) ||
              this.$efs.versionPickup
          }
        }
        if (isStale()) return

        if (
          deliveryVersion === this.$efs.versionDelivery &&
          nextPickupVersion === this.$efs.versionPickup
        ) {
          break
        }

        this.$efs.versionDelivery = deliveryVersion
        this.$efs.versionPickup = nextPickupVersion
        await this.loadZipMaster(isStale)
        if (isStale()) return
        await this.fetchFromToRegularWorkingDays(this.$efs.isFleaMarket)
        if (isStale()) return
        await this.adjustDeliveryDatesForFixedHolidays()
        if (isStale()) return
      }

      this.sanitizeSelectionsAfterMasterChange()
      await this.loadDeliveryPeakSeasonRules()
      if (this.$efs.isServiceLevel) {
        await this.fetchFromToOffices(isStale)
      }
    } finally {
      if (!isStale()) {
        this._isRefreshingMasters = false
        this.zip_from_pending = false
        this.zip_to_pending = false
        if (showOverlay) {
          $core.$loading.finish()
        }
      }
    }
  }

  public getPickupDateForVersion(): string | null {
    if (
      this.$efs.isServiceLevel &&
      this.serviceLevelDateMode === 'delivery' &&
      this.deliverySelectedDate &&
      !this.deliveryDateResetFlag
    ) {
      const earliest = this.findEarliestVisiblePickupCandidateDate(
        this.deliverySelectedDate,
      )
      if (earliest) return earliest
      return this.pickupSelectedDate || this.deliverySelectedDate
    }
    if (this.pickupDateResetFlag) return null
    return this.pickupSelectedDate
  }

  public getDeliveryDateForVersion(): string | null {
    if (this.isSameDayPickupAndDelivery && this.pickupSelectedDate) {
      return this.pickupSelectedDate
    }
    if (
      this.$efs.isServiceLevel &&
      this.serviceLevelDateMode === 'delivery' &&
      !this.deliveryDateResetFlag
    ) {
      return this.deliverySelectedDate
    }
    const eDate = this.deliveryDateAdjustedForFixedHolidays || this.deliveryDate || ''
    const fgDate =
      this.fgDeliveryDateAdjustedForFixedHolidays || this.fgDeliveryDate || ''
    const remote = this.remoteIslandDeliveryDateAdjusted || ''
    if (this.$efs.isServiceLevel) {
      const earliest = pickEarliestVisibleServiceLevelDate(eDate, fgDate, remote, {
        includeE: this.serviceLevelShowsERankDates,
        includeFg: this.serviceLevelShowsFgRankDates,
      })
      return earliest || this.pickupSelectedDate
    }
    const isRemoteIsland =
      this.zip_from_yhc_master?.isRemoteIsland === true ||
      this.zip_to_yhc_master?.isRemoteIsland === true
    return pickEstimateFormDeliveryDateForVersion({
      pickupDate: this.pickupSelectedDate,
      isSameDayPickupAndDelivery: this.isSameDayPickupAndDelivery,
      isRemoteIsland,
      remoteIslandDeliveryDateAdjusted: remote,
      displayedDeliveryDate: eDate,
    })
  }

  /**
   * 今日より後に発地の営業曜日が変わるとき、お預かり日下に出す注釈。
   * サービスレベル検索には出さない。
   */
  public get pickupWorkingDaysChangeNote(): string | null {
    if (this.$efs.isServiceLevel) return null
    if (!(this.zip_from || '').trim()) return null
    const today =
      toCalendarYmd(this.$efs.baseDatetime) || $core.$dayjs().format('YYYY-MM-DD')
    const changeYmd = findNextWorkingDaysChangeYmd(today, {
      periods: this.$efs.masterVersionPeriods,
      patternNameByVersionId: this.fromWorkingDayPatternByVersionId,
      records: this.regularWorkingDayRecords,
      useFallback: this.$efs.isFleaMarket,
    })
    if (!changeYmd) return null
    return `※${formatRevisionDateForNote(changeYmd)}以降、営業日が変わります。`
  }

  /**
   * 今日より後に着地の営業曜日が変わるとき、お届け日下に出す注釈。
   * サービスレベル検索には出さない。
   */
  public get deliveryWorkingDaysChangeNote(): string | null {
    if (this.$efs.isServiceLevel) return null
    if (!(this.zip_to || '').trim()) return null
    const today =
      toCalendarYmd(this.$efs.baseDatetime) || $core.$dayjs().format('YYYY-MM-DD')
    const changeYmd = findNextWorkingDaysChangeYmd(today, {
      periods: this.$efs.masterVersionPeriods,
      patternNameByVersionId: this.toWorkingDayPatternByVersionId,
      records: this.regularWorkingDayRecords,
      useFallback: this.$efs.isFleaMarket,
    })
    if (!changeYmd) return null
    return `※${formatRevisionDateForNote(changeYmd)}より営業日が変わります`
  }

  private get serviceLevelShowsERankDates(): boolean {
    if (!this.$efs.isServiceLevel || this.$efs.isFleaMarket) return false
    const mode = this.serviceLevelRankDisplayMode
    return mode === 'both' || mode === 'e_only'
  }

  private get serviceLevelShowsFgRankDates(): boolean {
    if (!this.$efs.isServiceLevel) return false
    const mode = this.serviceLevelRankDisplayMode
    if (mode !== 'both' && mode !== 'fg_only') return false
    return this.isFgRankAvailable
  }

  /**
   * お届け日指定モードで画面に出るお預かり可能日のうち、一番早い日付。
   * 表示中ランク（E / FG）だけを対象にする。
   */
  private findEarliestVisiblePickupCandidateDate(deliveryDate: string): string | null {
    if (!deliveryDate || !this.pref_from_to_info_master) return null
    const includeE = this.serviceLevelShowsERankDates
    const includeFg = this.serviceLevelShowsFgRankDates
    if (!includeE && !includeFg) return null

    const Dday = $core.$dayjs(deliveryDate)
    const deliversByD = (
      pickupDate: string,
      sizeRank: 'sizeRank0NeedDays' | 'sizeRank1NeedDays',
    ): boolean => {
      const delivery = this.computeDeliveryDateForPickupDate(pickupDate, sizeRank)
      if (!delivery) return false
      return (
        $core.$dayjs(delivery).isBefore(Dday) ||
        $core.$dayjs(delivery).isSame(Dday, 'day')
      )
    }
    const isVisiblePickup = (pickupDate: string): boolean => {
      if (!this.isPickupWorkingDayForVersion(pickupDate)) return false
      if (includeE && deliversByD(pickupDate, 'sizeRank0NeedDays')) return true
      if (includeFg && deliversByD(pickupDate, 'sizeRank1NeedDays')) return true
      return false
    }

    const inRange: string[] = []
    for (let i = 1; i <= 14; i++) {
      const pickupDate = Dday.subtract(i, 'day').format('YYYY-MM-DD')
      if (isVisiblePickup(pickupDate)) inRange.push(pickupDate)
    }
    if (inRange.length > 0) {
      return inRange.reduce((earliest, date) => (date < earliest ? date : earliest))
    }

    for (let i = 1; i <= 28; i++) {
      const pickupDate = Dday.subtract(i, 'day').format('YYYY-MM-DD')
      if (isVisiblePickup(pickupDate)) return pickupDate
    }
    return null
  }

  private isPickupWorkingDayForVersion(dateStr: string): boolean {
    return this.isPickupWorkingDayForDate(dateStr)
  }

  private sanitizeSelectionsAfterMasterChange() {
    if (this.zip_from_yhc_master) {
      const fromBand = this.zip_from_yhc_master.timeBand
      const fromKeys =
        yhcEFTimeOptionsByTimeBandCode[fromBand]?.selectableTimeOptionKeys || []
      if (!fromKeys.includes(this.from_time_option)) {
        this.from_time_option = fromKeys.includes('none') ? 'none' : 'unable'
        this.from_time_option_use_jit = false
      }
      if (!yhcEFTimeOptionsByTimeBandCode[fromBand]?.justInTimeOption) {
        this.from_time_option_use_jit = false
      }
    }
    if (this.zip_to_yhc_master) {
      const toBand = this.zip_to_yhc_master.timeBand
      const toKeys =
        yhcEFTimeOptionsByTimeBandCode[toBand]?.selectableTimeOptionKeys || []
      if (!toKeys.includes(this.to_time_option)) {
        this.to_time_option = toKeys.includes('none') ? 'none' : 'unable'
        this.to_time_option_use_jit = false
      }
      if (!yhcEFTimeOptionsByTimeBandCode[toBand]?.justInTimeOption) {
        this.to_time_option_use_jit = false
      }
    }
    if (this.isSameDayPickupAndDelivery && !this.sameDayPickupAndDeliveryAvailable) {
      this.isSameDayPickupAndDelivery = false
    }
  }

  private async _fetchSpecialLeadTimes() {
    if (!this.zip_to_yhc_master) {
      return null
    }
    const swapped = this.zip_from_yhc_master
      ? {
          fromPrefCode: this.pref_to,
          toPrefCode: this.pref_from,
          toCity: this.zip_from_yhc_master.city,
          toTown: this.zip_from_yhc_master.town,
        }
      : null
    const filters = specialLeadTimeFilters(
      {
        fromPrefCode: this.pref_from,
        toPrefCode: this.pref_to,
        toCity: this.zip_to_yhc_master.city,
        toTown: this.zip_to_yhc_master.town,
      },
      swapped,
    )
    const results = await Promise.all(
      filters.map((filter) =>
        findRecordsByModelName('yhc_special_lead_times', { filter }),
      ),
    )
    return pickFirstDefined(results.map((rows) => rows[0] || null))
  }

  public sameDayPickupAndDeliveryUseChange(isSameDayDelivery: boolean) {
    this.isSameDayPickupAndDelivery = isSameDayDelivery
    if (this.isSameDayPickupAndDelivery) {
      // 当日自発着利用時は、お預かり日が翌々日以前であれば翌々日に変更する（お預かり日が未選択の場合は何もしない）
      if (
        this.pickupSelectedDate != null &&
        $core.$dayjs(this.pickupSelectedDate).isBefore($core.$dayjs().add(2, 'day'))
      ) {
        this.pickupSelectedDateTemp = this.pickupSelectedDate

        let newPickupDate = $core.$dayjs().add(2, 'day').format('YYYY-MM-DD')

        // 営業日パターンを考慮
        if (this.from_regular_working_days_master) {
          newPickupDate = calcEarliestPickupDate(
            newPickupDate,
            this.from_regular_working_days_master,
          )
        }

        this.pickupSelectedDate = newPickupDate
        // お届け日はお預かり日と同一日にする
      }
      if (!this._isRefreshingMasters) {
        void this.refreshMastersForSelectedDates()
      }
    } else {
      // 当日自発着利用を解除した場合は、お預かり日を元に戻す
      if (this.pickupSelectedDateTemp) {
        this.pickupSelectedDate = this.pickupSelectedDateTemp
        this.pickupSelectedDateTemp = null
      }
      if (!this._isRefreshingMasters) {
        void this.refreshMastersForSelectedDates()
      }
    }
  }

  private kickAutoSaveStateToLocalStorage() {
    if (this._autoSaveIntervalId) {
      clearInterval(this._autoSaveIntervalId)
    }
    this._autoSaveIntervalId = setInterval(() => {
      this.saveStateToLocalStorage()
    }, 1000)
  }

  // LocalStorage に user input の状態を 保存する
  saveStateToLocalStorage() {
    $core.$lsCache.set('yhcEFUserInputDataHandler', this.toJson())
  }

  toJson() {
    return {
      zip_from: this.zip_from,
      zip_to: this.zip_to,
      pref_from: this.pref_from,
      pref_to: this.pref_to,
      from_time_option: this.from_time_option,
      from_time_option_use_jit: this.from_time_option_use_jit,
      to_time_option: this.to_time_option,
      to_time_option_use_jit: this.to_time_option_use_jit,
      userAddedKazaiDataById: this.userAddedKazaiDataById,
      userAddedKazaiDataByIdForKaguidoOptionCollectionDestination:
        this.userAddedKazaiDataByIdForKaguidoOptionCollectionDestination,
      userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination:
        this.userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination,
      isSameDayPickupAndDelivery: this.isSameDayPickupAndDelivery,
      pickupDateResetFlag: this.pickupDateResetFlag,
      deliveryDateResetFlag: this.deliveryDateResetFlag,
    }
  }

  restoreStateFromLocalStorage() {
    const restored = $core.$lsCache.get('yhcEFUserInputDataHandler')
    if (restored) {
      this.zip_from = restored.zip_from || ''
      this.zip_to = restored.zip_to || ''
      this.from_time_option = restored.from_time_option || 'none'
      this.from_time_option_use_jit = restored.from_time_option_use_jit || false
      this.to_time_option = restored.to_time_option || 'none'
      this.to_time_option_use_jit = restored.to_time_option_use_jit || false
      this.userAddedKazaiDataById = Object.keys(
        restored.userAddedKazaiDataById || {},
      ).reduce((acc: { [id: string]: YhcEFInputOneKazaiTypeDataObject }, id) => {
        acc[id] = reactive(
          new YhcEFInputOneKazaiTypeDataObject(
            this.$efs,
            restored.userAddedKazaiDataById[id],
          ),
        ) as YhcEFInputOneKazaiTypeDataObject
        return acc
      }, {})
      this.userAddedKazaiDataByIdForKaguidoOptionCollectionDestination = Object.keys(
        restored.userAddedKazaiDataByIdForKaguidoOptionCollectionDestination || {},
      ).reduce(
        (
          acc: {
            [id: string]: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
          },
          id,
        ) => {
          acc[id] = reactive(
            new YhcEFInputOneKazaiTypeDataObjectForKaguidoOption(
              this.$efs,
              restored.userAddedKazaiDataByIdForKaguidoOptionCollectionDestination[id],
            ),
          ) as YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
          return acc
        },
        {},
      )
      this.userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination = Object.keys(
        restored.userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination || {},
      ).reduce(
        (
          acc: {
            [id: string]: YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
          },
          id,
        ) => {
          acc[id] = reactive(
            new YhcEFInputOneKazaiTypeDataObjectForKaguidoOption(
              this.$efs,
              restored.userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination[id],
            ),
          ) as YhcEFInputOneKazaiTypeDataObjectForKaguidoOption
          return acc
        },
        {},
      )
      this.isSameDayPickupAndDelivery = restored.isSameDayPickupAndDelivery || false
      this.pickupDateResetFlag = restored.pickupDateResetFlag || false
      this.deliveryDateResetFlag = restored.deliveryDateResetFlag || false
    }
  }

  /**
   * 家財金額 小計
   */
  get kazaiSubTotalPrice(): number {
    return Object.values(this.userAddedKazaiDataById).reduce((acc, kazaiData) => {
      return acc + kazaiData.totalPrice
    }, 0)
  }

  /**
   * 家具移動オプション(お預かり先) 小計
   */
  get kaguidoOptionCDTotalPrice(): number {
    return Object.values(
      this.userAddedKazaiDataByIdForKaguidoOptionCollectionDestination,
    ).reduce((acc, kazaiData) => {
      return acc + kazaiData.totalPrice
    }, 0)
  }

  /**
   * 家具移動オプション(お届け先) 小計
   */
  get kaguidoOptionDDTotalPrice(): number {
    return Object.values(
      this.userAddedKazaiDataByIdForKaguidoOptionDeliveryDestination,
    ).reduce((acc, kazaiData) => {
      return acc + kazaiData.totalPrice
    }, 0)
  }

  get fromZipCompany(): 'yhc' | 'ytc' | null {
    // isMerパラメータを取得
    const isMer = this.$efs?.isFleaMarket
    return judgeCompanyOfAreaFromYhcZipMaster(this.zip_from_yhc_master, isMer)
  }
  get toZipCompany(): 'yhc' | 'ytc' | null {
    // isMerパラメータを取得
    const isMer = this.$efs?.isFleaMarket
    return judgeCompanyOfAreaFromYhcZipMaster(this.zip_to_yhc_master, isMer)
  }

  /**
   * from, to option の追加料金算出
   */
  get fromTimeOptionSubtotalPrice(): number {
    // そもそも ytc area の場合 時間指定ができないので 料金もかからない
    if (this.fromZipCompany === 'ytc') {
      return 0
    }
    // 当日自発着利用時も時間指定できないので0円
    if (this.isSameDayPickupAndDelivery) {
      return 0
    }
    return calcSubtotalPriceForTimeOptionSelect(
      this.from_time_option,
      this.zip_from_yhc_master?.timeBand,
      this.from_time_option_use_jit,
      this.$efs.generalOptionsByKey,
    )
  }
  get toTimeOptionSubtotalPrice(): number {
    // そもそも ytc area の場合 時間指定ができないので 料金もかからない
    if (this.toZipCompany === 'ytc') {
      return 0
    }
    // 当日自発着利用時も時間指定できないので0円
    if (this.isSameDayPickupAndDelivery) {
      return 0
    }
    return calcSubtotalPriceForTimeOptionSelect(
      this.to_time_option,
      this.zip_to_yhc_master?.timeBand,
      this.to_time_option_use_jit,
      this.$efs.generalOptionsByKey,
    )
  }

  /**
   * 当日自発着利用料金
   */
  get sameDayPickupAndDeliveryPrice(): number {
    return this.isSameDayPickupAndDelivery
      ? this.$efs.generalOptionsByKey['same_day_delivery']?.price || 0
      : 0
  }

  /**
   * 基本のお届け日数（マスタデータから取得）
   * お届け日選択モードかつ離島かつ特殊リードタイムがある場合は特殊リードタイムマスタの値を使用（-1 しない）。
   * @private
   */
  private _getBaseDeliverNeedDays(
    sizeRank: 'sizeRank0NeedDays' | 'sizeRank1NeedDays',
  ): number | null {
    if (!this.pref_from_to_info_master) {
      return null
    }
    const isRemoteIsland =
      this.zip_from_yhc_master?.isRemoteIsland === true ||
      this.zip_to_yhc_master?.isRemoteIsland === true
    const useSpecialLeadTime =
      this.$efs.isServiceLevel &&
      this.serviceLevelDateMode === 'delivery' &&
      isRemoteIsland &&
      this.special_lead_times != null

    if (useSpecialLeadTime && this.special_lead_times) {
      const days = Number(this.special_lead_times[sizeRank])
      if (Number.isFinite(days) && days > 0) {
        return days
      }
    }
    return this.pref_from_to_info_master[sizeRank] - 1
  }

  /**
   * 営業日パターンマスタを考慮した総お届け日数の計算（内部メソッド）
   * @param sizeRank サイズランク（Eランク以下 or FGランク）
   * @param overridePickupDate お預かり日を引数で渡す場合（サービスレベル検索のお預かり可能日逆算用）。未指定時は this.pickupSelectedDate を使用
   * @private
   */
  private _calculateTotalDeliveryDays(
    sizeRank: 'sizeRank0NeedDays' | 'sizeRank1NeedDays',
    overridePickupDate?: string,
  ): number | null {
    const basePickupDate = overridePickupDate ?? this.pickupSelectedDate
    if (!basePickupDate) return null

    // 基本のお届け日数
    let totalDays = this._getBaseDeliverNeedDays(sizeRank)

    if (totalDays === null) {
      return null
    }

    try {
      // 1. 郵便番号マスタの固定リードタイム加算（発）
      totalDays += parseAdditionalLeadTimeDays(
        this.zip_from_yhc_master?.additionalLeadTimeDaysFrom,
      )

      // 2. 郵便番号マスタの固定リードタイム加算（着）
      totalDays += parseAdditionalLeadTimeDays(
        this.zip_to_yhc_master?.additionalLeadTimeDaysTo,
      )

      // 3. フリマ用ページの場合は+1日加算
      if (this.$efs.isFleaMarket) {
        totalDays += 1
      }

      // 3b. 法人向けリードタイム+1日ページ（サービスレベル検索専用）
      if (this.$efs.isCorporationLeadTimePlusOne) {
        totalDays += 1
      }

      // 4. お届け日がお届け先の定休日だった場合の加算日数
      if (this.to_regular_working_days_master) {
        const tentativeDeliveryDate = $core
          .$dayjs(basePickupDate)
          .add(totalDays, 'day')
          .format('YYYY-MM-DD')

        const additionalDays = addingLeadTimeDaysByRegularWorkingDays(
          tentativeDeliveryDate,
          this.to_regular_working_days_master,
        )
        totalDays += additionalDays
      }

      // 5. お届け日が年末年始休業日だった場合の加算日数
      if (this.fixed_holidays && this.fixed_holidays.length > 0) {
        let tentativeDeliveryDate = $core.$dayjs(basePickupDate).add(totalDays, 'day')

        for (let i = 0; i < 30; i++) {
          const monthDay = tentativeDeliveryDate.format('MM-DD')
          const isFixedHoliday = this.fixed_holidays.some((holiday) => {
            const startMD = holiday.start_month_day
            const endMD = holiday.end_month_day
            if (!startMD || !endMD) return false

            if (startMD > endMD) {
              return monthDay >= startMD || monthDay <= endMD
            } else {
              return monthDay >= startMD && monthDay <= endMD
            }
          })

          if (!isFixedHoliday) {
            if (this.to_regular_working_days_master) {
              const regularWeekdays =
                this.to_regular_working_days_master.regular_weekdays || []
              const hasRegularPattern =
                regularWeekdays.length > 0 && regularWeekdays.length < 7

              if (hasRegularPattern) {
                const dayOfWeek = tentativeDeliveryDate.format('dd')
                if (regularWeekdays.includes(dayOfWeek)) {
                  break
                }
              } else {
                break
              }
            } else {
              break
            }
          }

          totalDays += 1
          tentativeDeliveryDate = tentativeDeliveryDate.add(1, 'day')
        }
      }
    } catch (error) {
      console.error(
        '営業日パターンマスタによるリードタイム計算でエラーが発生しました',
        error,
      )
      // エラーが発生した場合は基本日数を返す
      return this._getBaseDeliverNeedDays(sizeRank)
    }

    return totalDays
  }

  /**
   * 合計金額
   */
  get totalPrice(): number {
    if (this.hasError) {
      return 0
    }
    if (!this.kazaiSubTotalPrice) {
      return 0
    }
    return (
      this.kazaiSubTotalPrice +
      this.fromTimeOptionSubtotalPrice +
      this.toTimeOptionSubtotalPrice +
      this.sameDayPickupAndDeliveryPrice +
      (this.isKaguidoOptionAvailableForFromArea ? this.kaguidoOptionCDTotalPrice : 0) +
      (this.isKaguidoOptionAvailableForToArea ? this.kaguidoOptionDDTotalPrice : 0) +
      (this.deliveryPeakSeasonRule ? this.deliveryPeakSeasonRule.additionalPrice : 0)
    )
  }

  /**
   * 大型家財 (特殊家財) に対応できる状態かどうか？
   *
   * - zipMaster.deliveryAreaFlag に 一つでも 2 が含まれている場合には false
   */
  get canAcceptLargeSizeKazai(): boolean {
    if (
      this.zip_from_yhc_master?.deliveryAreaFlag == '2' ||
      this.zip_to_yhc_master?.deliveryAreaFlag == '2'
    ) {
      return false
    }
    return true
  }

  /**
   * 大型家財 に区分けされる ユーザが入力した家財データ
   */
  get largeSizeUserAddedKazai(): YhcEFInputOneKazaiTypeDataObject[] {
    return Object.values(this.userAddedKazaiDataById).filter(
      (kazaiData) => kazaiData.isLargeSize,
    )
  }

  /**
   * お預かり日 の表示
   *
   * - そもそもYTCエリアである場合には \"ご依頼時に調整\" と表示
   * - YHC提供エリア である場合にのみ 本日 + 1日 の日付を返す
   * - リセットフラグがtrueの場合は「----年--月--日」を表示
   */
  get pickupDateDisplayLabel(): string {
    if (!this.zip_from_yhc_master) {
      return ''
    }
    // リセットフラグがtrueの場合、特殊な表示形式を返す
    if (this.pickupDateResetFlag) {
      return '----年--月--日'
    }
    // エラー or warning メッセージがないなら 算出日付を返す（お預かり日がnullの場合はプレースホルダ表示）
    return (
      this.pickupDateDisplayErrorMessage ||
      (!this.$efs.isServiceLevel ? this.pickupDateDisplayWarningMessage : false) ||
      (this.pickupSelectedDate != null
        ? $core.$dayjs(this.pickupSelectedDate).format(dateFormatJp)
        : '----年--月--日')
    )
  }

  // \"家財提供不能\" エリア が含まれているかどうか
  get isContainUnavailableArea(): boolean {
    // isMerパラメータを取得（fetchFromToRegularWorkingDaysに渡される値）
    const isMer = this.$efs?.isFleaMarket

    if (isMer) {
      // フリマ用ページの場合、0と2も3と同じ扱い
      return (
        this.zip_from_yhc_master?.deliveryAreaFlag == '0' ||
        this.zip_from_yhc_master?.deliveryAreaFlag == '2' ||
        this.zip_from_yhc_master?.deliveryAreaFlag == '3' ||
        this.zip_to_yhc_master?.deliveryAreaFlag == '0' ||
        this.zip_to_yhc_master?.deliveryAreaFlag == '2' ||
        this.zip_to_yhc_master?.deliveryAreaFlag == '3'
      )
    } else {
      // 通常の場合は従来通り3のみチェック
      return (
        this.zip_from_yhc_master?.deliveryAreaFlag == '3' ||
        this.zip_to_yhc_master?.deliveryAreaFlag == '3'
      )
    }
  }
  // \"特殊家財不能\" エリア が含まれているかどうか
  get isContainLargeSizeKazaiUnavailableArea(): boolean {
    return (
      this.zip_from_yhc_master?.deliveryAreaFlag == '2' ||
      this.zip_to_yhc_master?.deliveryAreaFlag == '2'
    )
  }
  // \"ちょこっと模様替え\"オプションが利用可能かどうか
  get isKaguidoOptionAvailable(): boolean {
    // isMerパラメータを取得
    const isMer = this.$efs?.isFleaMarket

    if (isMer) {
      // フリマ用ページの場合、0と2も3と同じ扱い
      return !(
        (this.zip_from_yhc_master?.deliveryAreaFlag == '0' ||
          this.zip_from_yhc_master?.deliveryAreaFlag == '2' ||
          this.zip_from_yhc_master?.deliveryAreaFlag == '3') &&
        (this.zip_to_yhc_master?.deliveryAreaFlag == '0' ||
          this.zip_to_yhc_master?.deliveryAreaFlag == '2' ||
          this.zip_to_yhc_master?.deliveryAreaFlag == '3')
      )
    } else {
      // 通常の場合は従来通り2と3のみチェック
      return !(
        (this.zip_from_yhc_master?.deliveryAreaFlag == '2' ||
          this.zip_from_yhc_master?.deliveryAreaFlag == '3') &&
        (this.zip_to_yhc_master?.deliveryAreaFlag == '2' ||
          this.zip_to_yhc_master?.deliveryAreaFlag == '3')
      )
    }
  }
  // \"ちょこっと模様替え\"オプションがお預かり先で利用可能かどうか
  get isKaguidoOptionAvailableForFromArea(): boolean {
    // isMerパラメータを取得
    const isMer = this.$efs?.isFleaMarket

    if (isMer) {
      // フリマ用ページの場合、0と2も3と同じ扱い
      return !(
        this.zip_from_yhc_master?.deliveryAreaFlag == '0' ||
        this.zip_from_yhc_master?.deliveryAreaFlag == '2' ||
        this.zip_from_yhc_master?.deliveryAreaFlag == '3'
      )
    } else {
      // 通常の場合は従来通り2と3のみチェック
      return !(
        this.zip_from_yhc_master?.deliveryAreaFlag == '2' ||
        this.zip_from_yhc_master?.deliveryAreaFlag == '3'
      )
    }
  }
  // \"ちょこっと模様替え\"オプションがお届け先で利用可能かどうか
  get isKaguidoOptionAvailableForToArea(): boolean {
    // isMerパラメータを取得
    const isMer = this.$efs?.isFleaMarket

    if (isMer) {
      // フリマ用ページの場合、0と2も3と同じ扱い
      return !(
        this.zip_to_yhc_master?.deliveryAreaFlag == '0' ||
        this.zip_to_yhc_master?.deliveryAreaFlag == '2' ||
        this.zip_to_yhc_master?.deliveryAreaFlag == '3'
      )
    } else {
      // 通常の場合は従来通り2と3のみチェック
      return !(
        this.zip_to_yhc_master?.deliveryAreaFlag == '2' ||
        this.zip_to_yhc_master?.deliveryAreaFlag == '3'
      )
    }
  }

  /**
   * FGランク（大型家財）が利用可能かどうか
   * - お預かり先・お届け先のいずれかが「特殊家財不能(2)」または「家財提供不能(3)」エリアでないこと
   */
  get isFgRankAvailable(): boolean {
    // 郵便番号マスターが読み込まれていない場合は true (エラー表示は他のプロパティに任せる)
    if (!this.zip_from_yhc_master || !this.zip_to_yhc_master) {
      return true
    }
    // 既存の判定用プロパティを流用
    if (this.isContainLargeSizeKazaiUnavailableArea || this.isContainUnavailableArea) {
      return false
    }
    return true
  }

  /**
   * お預かり日 エラーメッセージを返す
   *
   * ## ロジック
   * - 1. お預かりエリア or お届けエリア が \"家財提供不能\" エリア である場合に は \"サービスエリア外\" と表示
   * - 2. お預かりエリア or お届けエリア が \"特殊家財不能\" エリア であり かつ 大型家財がある場合には \"サービスエリア外\" と表示
   * - 3. お預かりエリアが \"特殊家財不能\" エリア であり お預かり先が 北海道 エリアである場合には、 \"お申し込み時に日程調整が必要\" と表示
   * - 4. お預かりエリアが \"YTC提供\" エリア で お預かり先 が 沖縄県 or 北海道  エリアである場合には、 \"お申し込み時に日程調整が必要\" と表示
   */
  get pickupDateDisplayErrorMessage(): string | null {
    if (
      (this.zip_from || this.zip_to) &&
      !this.$efs.versionPickup &&
      this.getPickupDateForVersion()
    ) {
      return dateDisplayErrorMessages.masterVersionNotFound
    }
    // 不正な郵便番号である場合
    if (this.zip_from && !this.zip_from_yhc_master) {
      return this.$efs.versionPickup
        ? dateDisplayErrorMessages.zipNotInMasterVersion
        : dateDisplayErrorMessages.invalidZipCode
    }
    if (this.zip_to && !this.zip_to_yhc_master) {
      return this.$efs.versionDelivery || this.$efs.versionPickup
        ? dateDisplayErrorMessages.zipNotInMasterVersion
        : dateDisplayErrorMessages.invalidZipCode
    }
    // 1. お預かりエリア or お届けエリア が \"家財提供不能\" エリア である場合に は \"サービスエリア外\" と表示
    if (this.isContainUnavailableArea) {
      return dateDisplayErrorMessages.unavailableArea
    }
    // 2. お預かりエリア or お届けエリア が \"特殊家財不能\" エリア であり かつ 大型家財がある場合には \"サービスエリア外\" と表示
    if (
      this.isContainLargeSizeKazaiUnavailableArea &&
      this.largeSizeUserAddedKazai.length
    ) {
      return dateDisplayErrorMessages.unavailableBecauseOfIncludingLargeSizeKazai
    }
    return null
  }
  get pickupDateDisplayWarningMessage(): string {
    // 3. お預かりエリアが \"特殊家財不能\" エリア であり お預かり先が 北海道 エリアである場合には、 \"お申し込み時に日程調整が必要\" と表示
    if (
      this.zip_from_yhc_master?.deliveryAreaFlag == '2' &&
      yhcAreaIsInHokkaido(this.pref_from)
    ) {
      return dateDisplayErrorMessages.needDateAdjustDueToHokkaido
    }

    // 4. お預かりエリアが \"YTC提供\" エリア で お預かり先 が 沖縄県 or 北海道  エリアである場合には、 \"お申し込み時に日程調整が必要\" と表示
    if (
      this.fromZipCompany === 'ytc' &&
      (yhcAreaIsInHokkaido(this.pref_from) || yhcAreaIsInOkinawa(this.pref_from))
    ) {
      return dateDisplayErrorMessages.needDateAdjustDueToHokkaidoOrOkinawa
    }
    return ''
  }

  /**
   * お届け日数
   * ※ DB 上は 本日を 1日目 としているため、 -1 して返却している
   * ※ 営業日パターンマスタを考慮した実際のお届け日数
   */
  get deliverNeedDays(): number {
    const sizeRank =
      this.largeSizeUserAddedKazai.length > 0 ? 'sizeRank1NeedDays' : 'sizeRank0NeedDays'
    // エラーや警告がある場合、または当日自発着の場合は基本日数を返す
    if (
      !this.pref_from_to_info_master ||
      this.deliveryDateDisplayErrorMessage ||
      (!this.$efs.isServiceLevel && this.deliveryDateDisplayWarningMessage) ||
      this.isSameDayPickupAndDelivery
    ) {
      return this._getBaseDeliverNeedDays(sizeRank) ?? 0
    }

    try {
      // 営業日パターンマスタを考慮した総日数を返す
      return this._calculateTotalDeliveryDays(sizeRank) ?? 0
    } catch (error) {
      console.error('お届け日数計算でエラーが発生しました', error)
      return this._getBaseDeliverNeedDays(sizeRank) ?? 0
    }
  }

  /**
   * お届け日をyyyy-MM-dd形式で返す
   */
  get deliveryDate(): string {
    if (!this.pref_from_to_info_master) {
      return ''
    }

    // エラーや警告がある場合、または当日自発着の場合、またはお預かり日がnull/空の場合
    if (
      this.deliveryDateDisplayErrorMessage ||
      (!this.$efs.isServiceLevel && this.deliveryDateDisplayWarningMessage) ||
      this.isSameDayPickupAndDelivery ||
      !this.pickupSelectedDate ||
      this.pickupDateResetFlag
    ) {
      return this.pickupSelectedDate || ''
    }

    try {
      // deliverNeedDaysから総日数を取得（ロジックの重複を避ける）
      const totalDays = this.deliverNeedDays
      if (totalDays === null) return ''

      // 総日数をもとにお届け日を計算
      return $core
        .$dayjs(this.pickupSelectedDate)
        .add(totalDays, 'day')
        .format('YYYY-MM-DD')
    } catch (error) {
      console.error('お届け日計算でエラーが発生しました', error)

      // エラーが発生した場合は基本日数で計算
      const sizeRank =
        this.largeSizeUserAddedKazai.length > 0
          ? 'sizeRank1NeedDays'
          : 'sizeRank0NeedDays'
      const baseDays = this._getBaseDeliverNeedDays(sizeRank)
      if (baseDays === null) return ''
      return $core
        .$dayjs(this.pickupSelectedDate)
        .add(baseDays, 'day')
        .format('YYYY-MM-DD')
    }
  }

  /**
   * お届け日 の表示
   * - そもそもYTCエリアである場合には \"ご依頼時に調整\" と表示
   * - YHC提供エリア である場合にのみ
   *   - 大型家財があるかどうか で お預かり日 + N日 (15時までのお預かり) と表示
   * - 当日自発着利用時は、お預かり日と同一日にする
   */
  get deliveryDateDisplayLabel(): string {
    if (!this.pref_from_to_info_master) {
      return ''
    }

    // お預かり日がnullまたはリセットフラグがtrueの場合は特殊な表示形式を返す
    if (
      this.pickupDateResetFlag ||
      !this.pickupSelectedDate ||
      this.deliveryDateDisplayErrorMessage ||
      (!this.$efs.isServiceLevel && this.deliveryDateDisplayWarningMessage)
    ) {
      return '----年--月--日'
    }

    if (this.isSameDayPickupAndDelivery) {
      return $core.$dayjs(this.pickupSelectedDate).format(dateFormatJp)
    }

    // deliveryDateを使用して日付を取得（ロジックの重複を避ける）
    return $core.$dayjs(this.deliveryDate).format(dateFormatJp)
  }

  /**
   * FGランクのお届け日数
   */
  get fgDeliverNeedDays(): number {
    if (
      !this.pref_from_to_info_master ||
      this.deliveryDateDisplayErrorMessage ||
      (!this.$efs.isServiceLevel && this.deliveryDateDisplayWarningMessage) ||
      this.isSameDayPickupAndDelivery
    ) {
      return this._getBaseDeliverNeedDays('sizeRank1NeedDays') ?? 0
    }

    try {
      return this._calculateTotalDeliveryDays('sizeRank1NeedDays') ?? 0
    } catch (error) {
      console.error('FGランクお届け日数計算でエラーが発生しました', error)
      return this._getBaseDeliverNeedDays('sizeRank1NeedDays') ?? 0
    }
  }

  /**
   * FGランクのお届け日をyyyy-MM-dd形式で返す
   */
  get fgDeliveryDate(): string {
    if (!this.pref_from_to_info_master) {
      return ''
    }

    if (
      this.deliveryDateDisplayErrorMessage ||
      (!this.$efs.isServiceLevel && this.deliveryDateDisplayWarningMessage) ||
      this.isSameDayPickupAndDelivery ||
      !this.pickupSelectedDate ||
      this.pickupDateResetFlag
    ) {
      return this.pickupSelectedDate || ''
    }

    try {
      const totalDays = this.fgDeliverNeedDays
      if (totalDays === null) return ''

      return $core
        .$dayjs(this.pickupSelectedDate)
        .add(totalDays, 'day')
        .format('YYYY-MM-DD')
    } catch (error) {
      console.error('FGランクお届け日計算でエラーが発生しました', error)
      const baseDays = this._getBaseDeliverNeedDays('sizeRank1NeedDays')
      if (baseDays === null) return ''
      return $core
        .$dayjs(this.pickupSelectedDate)
        .add(baseDays, 'day')
        .format('YYYY-MM-DD')
    }
  }

  /**
   * 指定したお預かり日からお届け日を計算する（サービスレベル検索のお預かり可能日逆算用）
   * @param pickupDate お預かり日 YYYY-MM-DD
   * @param sizeRank サイズランク（Eランク以下: sizeRank0NeedDays / FGランク: sizeRank1NeedDays）
   * @returns お届け日 YYYY-MM-DD。計算できない場合は空文字
   */
  public computeDeliveryDateForPickupDate(
    pickupDate: string,
    sizeRank: 'sizeRank0NeedDays' | 'sizeRank1NeedDays',
  ): string {
    if (!this.pref_from_to_info_master || !pickupDate) return ''
    try {
      const totalDays = this._calculateTotalDeliveryDays(sizeRank, pickupDate)
      if (totalDays === null) return ''
      return $core.$dayjs(pickupDate).add(totalDays, 'day').format('YYYY-MM-DD')
    } catch (error) {
      console.error(
        '[YhcEFUserInputDataHandler] computeDeliveryDateForPickupDate でエラー',
        error,
      )
      return ''
    }
  }

  /**
   * 当日自発着可能かどうか
   * - お預かり先、お届け先双方のエリアが当日配送可能エリアである
   * - 且つ、お預かり先とお届け先の都市が同じである
   */
  get sameDayPickupAndDeliveryAvailable(): boolean {
    if (!this.zip_from_yhc_master || !this.zip_to_yhc_master) {
      return false
    }
    return (
      this.zip_from_yhc_master.sameDayDeliveryEnabled &&
      this.zip_to_yhc_master.sameDayDeliveryEnabled &&
      this.zip_from_yhc_master.city === this.zip_to_yhc_master.city
    )
  }

  /**
   * お届け日の エラーメッセージを返す
   * - 現時点では \"お預かり日のエラーメッセージ\" と同じ
   */
  get deliveryDateDisplayErrorMessage(): string {
    return this.pickupDateDisplayErrorMessage ?? ''
  }
  get deliveryDateDisplayWarningMessage(): string {
    return this.pickupDateDisplayWarningMessage
  }

  /**
   * エラーが含まれる ユーザが入力した家財データ
   */
  get errorStateUserAddedKazai(): YhcEFInputOneKazaiTypeDataObject[] {
    return Object.values(this.userAddedKazaiDataById).filter(
      (kazaiData) => kazaiData.hasError,
    )
  }

  get hasPickupAndDeliveryError(): boolean {
    return !!(this.pickupDateDisplayErrorMessage || this.deliveryDateDisplayErrorMessage)
  }

  /**
   * エラーが含まれているかどうか
   */
  get hasError(): boolean {
    if (this.errorStateUserAddedKazai.length) {
      return true
    }
    return !!(this.pickupDateDisplayErrorMessage || this.deliveryDateDisplayErrorMessage)
  }
}

const dateFormatJp = 'YYYY年MM月DD日'

/**
 * 郵便番号のマスタデータレコードから、YHC提供エリアであるかどうかを判定する
 * @param yhcMaster
 * @param isMer フリマ用ページかどうか
 */
export const judgeCompanyOfAreaFromYhcZipMaster = (
  yhcMaster: ModelYhcMastersByZipcode | null,
  isMer: boolean = false,
): 'yhc' | 'ytc' | null => {
  if (!yhcMaster?.deliveryAreaFlag) {
    return null
  }

  if (isMer) {
    // フリマ用ページの場合、0と2も3と同じ扱い
    return yhcMaster.deliveryAreaFlag == '1'
      ? 'yhc'
      : yhcMaster.deliveryAreaFlag == '0' ||
          yhcMaster.deliveryAreaFlag == '2' ||
          yhcMaster.deliveryAreaFlag == '3'
        ? null
        : 'ytc'
  } else {
    // 通常の場合は従来通り3のみnull
    return yhcMaster.deliveryAreaFlag == '1'
      ? 'yhc'
      : yhcMaster.deliveryAreaFlag == '3'
        ? null
        : 'ytc'
  }
}

/**
 * 時間帯指定の選択肢
 *
 * ## \"時間帯指定\" の選択肢を出す Logic
 * - 配送元, 先 それぞれの zipMaster 内の \"時間帯区分\" `timeBand` により 選択肢 & 料金 が決定される
 *   - 0: ４区分 (指定なし, 午前, 12-15時, 15-18時, 18-21時)
 *   - 1: ３区分[無料] (指定なし, 午前, 12-18時, 18-21時)
 *   - 2: ３区分[有料] (指定なし, +5,500円)
 *   - 3: 指定不可
 *   - 4: ４区分 2027-01-01〜 (指定なし, 8-12時, 12-16時, 16-19時, 18-21時。各枠 +1,100円税込)
 *   - 5: 指定不可 2027-01-01〜 (指定不可 + 1時間枠 JIT)
 */
export const yhcEFTimeOptions = {
  unable: { value: 'unable', label: '指定不可' },
  none: { value: 'none', label: '指定なし' },
  am: { value: 'am', label: '午前' },
  am8_12: { value: 'am8_12', label: '8-12時' },
  pm12_15: { value: 'pm12_15', label: '12-15時' },
  pm15_18: { value: 'pm15_18', label: '15-18時' },
  pm12_16: { value: 'pm12_16', label: '12-16時' },
  pm16_19: { value: 'pm16_19', label: '16-19時' },
  pm12_18: { value: 'pm12_18', label: '12-18時' },
  pm18_21: { value: 'pm18_21', label: '18-21時' },
}

export type YhcEFTimeOptionKey = keyof typeof yhcEFTimeOptions

const timeOptionKeysFor3Options: YhcEFTimeOptionKey[] = [
  'none',
  'am',
  'pm12_18',
  'pm18_21',
]
const timeOptionKeysFor4Options: YhcEFTimeOptionKey[] = [
  'none',
  'am',
  'pm12_15',
  'pm15_18',
  'pm18_21',
]
const timeOptionKeysFor4Options2027: YhcEFTimeOptionKey[] = [
  'none',
  'am8_12',
  'pm12_16',
  'pm16_19',
  'pm18_21',
]

export type YhcEFTimeOptionsByTimeBandCode = {
  [K in YhcMastersByZipcodeTimeBand]: {
    label: string
    selectableTimeOptionKeys: YhcEFTimeOptionKey[]
    // YhcOptionsKey は string であるが、これが 価格の key になる
    additionalPriceByTimeBand?: {
      [K in YhcEFTimeOptionKey]?:
        | YhcOptionsKey
        | 'time_band_additional_price_1100'
        | 'time_band_additional_price_2200'
    }
    justInTimeOption?: {
      price: YhcOptionsKey
    }
  }
}

/**
 * 配送元, 先 それぞれの zipMaster 内の \"時間帯区分\" `timeBand` により 選択肢 & 料金 が決定される
 */
export const yhcEFTimeOptionsByTimeBandCode: YhcEFTimeOptionsByTimeBandCode = {
  '0': {
    label: '4区分',
    selectableTimeOptionKeys: timeOptionKeysFor4Options,
    justInTimeOption: { price: 'just_on_time_0' },
    additionalPriceByTimeBand: {
      am: 'time_band_additional_price_1100',
    },
  },
  '1': {
    label: '3区分[無料]',
    selectableTimeOptionKeys: timeOptionKeysFor3Options,
    justInTimeOption: { price: 'just_on_time_1' },
    additionalPriceByTimeBand: {
      am: 'time_band_additional_price_1100',
    },
  },
  '2': {
    label: '3区分[有料]',
    selectableTimeOptionKeys: timeOptionKeysFor3Options,
    additionalPriceByTimeBand: {
      am: 'specified_time_slot',
      pm12_18: 'specified_time_slot',
      pm18_21: 'specified_time_slot',
    },
  },
  '3': {
    label: '指定不可',
    selectableTimeOptionKeys: ['unable'],
  },
  '4': {
    label: '4区分（2027-01-01〜）',
    selectableTimeOptionKeys: timeOptionKeysFor4Options2027,
    justInTimeOption: { price: 'just_on_time_0' },
    additionalPriceByTimeBand: {
      am8_12: 'time_band_additional_price_1100',
      pm12_16: 'time_band_additional_price_1100',
      pm16_19: 'time_band_additional_price_1100',
      pm18_21: 'time_band_additional_price_1100',
    },
  },
  '5': {
    label: '指定不可（2027-01-01〜）',
    selectableTimeOptionKeys: ['unable'],
    justInTimeOption: { price: 'just_on_time_unable' },
  },
}

export const calcSubtotalPriceForTimeOptionSelect = (
  selectedTimeOption: YhcEFTimeOptionKey,
  timeBand: YhcMastersByZipcodeTimeBand | undefined,
  useJit: boolean,
  generalOptionByKey: YhcEstimateFormFrontService['generalOptionsByKey'],
) => {
  if (!timeBand) {
    return 0
  }
  const timeOptions = yhcEFTimeOptionsByTimeBandCode[timeBand]
  const timeOptionKey =
    selectedTimeOption === 'unable'
      ? undefined
      : timeOptions?.additionalPriceByTimeBand?.[selectedTimeOption]
  const timeOptionPrice =
    (timeOptionKey != null
      ? generalOptionByKey[timeOptionKey as YhcOptionsKey]?.price
      : undefined) || 0
  const jitKey = timeOptions?.justInTimeOption?.price
  const jitPrice = useJit
    ? (jitKey != null ? generalOptionByKey[jitKey]?.price : undefined) || 0
    : 0
  return timeOptionPrice + jitPrice
}

/**
 * お預かり日の最短日数
 * - 現在時刻が 18:00 前であれば 1, それ以外は 2
 * - 当日自発着利用時は、さらに+1日する
 */
/**
 * お預かり日の最短日数を計算
 * @param isSameDayPickupAndDelivery 当日自発着利用フラグ
 * @param currentDatePeakSeasonRule 現在日の繁忙期ルール
 */
const minPickupNeedDays = (
  isSameDayPickupAndDelivery: boolean,
  currentDatePeakSeasonRule?: ModelYhcKazaiDeliveryPeakSeasonRules,
) => {
  const now = $core.$dayjs()
  const hour = now.hour()
  const today = now.format('YYYY-MM-DD')

  // 現在日が繁忙期の場合
  const isPeakSeason =
    currentDatePeakSeasonRule &&
    today >= currentDatePeakSeasonRule.startDate &&
    today <= currentDatePeakSeasonRule.endDate

  if (isPeakSeason) {
    // 15時を基準に判定
    let minPickupNeedDay = hour < 15 ? 2 : 3
    return isSameDayPickupAndDelivery ? minPickupNeedDay + 1 : minPickupNeedDay
  }

  // 通常期の場合
  // 18時を基準に判定
  let minPickupNeedDay = hour < 18 ? 1 : 2
  return isSameDayPickupAndDelivery ? minPickupNeedDay + 1 : minPickupNeedDay
}
